// §5.7 / D-034 rule 2 — **the stable portal layer**. The single most important structural rule
// in this codebase.
//
// Re-parenting an iframe reloads it, so a re-render that relocates a window's node would
// silently restart a user's editor mid-edit. Therefore:
//
//   • one flat, append-only list of `.win-host` divs, keyed by `instanceKey`
//   • never reordered, never re-keyed, never conditionally unmounted
//   • stacking is `z-index`, geometry is `transform` + `width`/`height`, desktop membership is
//     a CSS class, minimize is `visibility` — none of them are React reconciliation
//   • closing a window is the only thing that removes an entry
//
// Each host has exactly two stable children: `.win-chrome`, the portal target the skin draws
// its frame into, and `.win-content`, where the app is mounted. Both are created once with the
// host and never touched again, so a skin change re-renders chrome without going anywhere near
// the app.
//
// Layout is written imperatively from a store subscription rather than through props, so a
// focus change or a drag commit costs zero React renders. React re-renders only when the *set*
// of live instance keys changes.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Win } from '@hangar/shared';
import type { WmStore } from '@/kernel/wm/store';
import { noteRender } from './debug';

export const WIN_HOST_CLASS = 'win-host';
export const WIN_HOST_OTHER_DESKTOP_CLASS = 'win-host--other-desktop';
export const WIN_HOST_MINIMIZED_CLASS = 'win-host--minimized';

interface HostEntry {
  instanceKey: string;
  winId: string;
  appId: string;
}

/** The two stable children of a host, handed to whoever needs them. */
export interface HostSlots {
  host: HTMLDivElement;
  chrome: HTMLDivElement;
  content: HTMLDivElement;
}

export interface AppHostProps {
  wm: WmStore;
  /** Called once, when a window's host element first exists. Mount the app into it (§9.1). */
  onHostAttached?: (win: Win, slots: HostSlots) => void;
  /** Called once, when the window closed and its host element went away. */
  onHostDetached?: (instanceKey: string) => void;
  /** Notified whenever the live set of hosts changes, so frames can be portalled into them. */
  onSlotsChanged?: (slots: ReadonlyMap<string, HostSlots>) => void;
}

/** Everything the layer writes onto a host element, in one place. */
export function applyHostLayout(
  node: HTMLElement,
  win: Win,
  activeDesktopId: string,
  focusedId: string | null,
): void {
  node.style.transform = `translate3d(${win.rect.x}px, ${win.rect.y}px, 0)`;
  node.style.width = `${win.rect.w}px`;
  node.style.height = `${win.rect.h}px`;
  node.style.zIndex = String(win.z);

  const minimized = win.state === 'minimized';
  node.classList.toggle(WIN_HOST_MINIMIZED_CLASS, minimized);
  node.classList.toggle(WIN_HOST_OTHER_DESKTOP_CLASS, win.desktopId !== activeDesktopId);
  node.dataset['desktopId'] = win.desktopId;
  node.dataset['winState'] = win.state;
  node.dataset['focused'] = String(focusedId === win.id);
}

export function AppHost({ wm, onHostAttached, onHostDetached, onSlotsChanged }: AppHostProps) {
  const [entries, setEntries] = useState<HostEntry[]>([]);
  const slots = useRef(new Map<string, HostSlots>());
  const refCallbacks = useRef(new Map<string, (node: HTMLDivElement | null) => void>());
  const attached = useRef(new Set<string>());

  const layout = useCallback(() => {
    const { windows, activeDesktopId, focusedId } = wm.getState();
    for (const win of Object.values(windows)) {
      const slot = slots.current.get(win.instanceKey);
      if (slot) applyHostLayout(slot.host, win, activeDesktopId, focusedId);
    }
  }, [wm]);

  // Keep the entry list in step with the store: append what is new, drop what closed, and
  // never touch the order of what stays.
  useEffect(() => {
    const sync = (): void => {
      const windows = Object.values(wm.getState().windows);

      // Forget the ref callback of a window that has actually **closed** — and only then.
      // Dropping it on *detach* would hand React a fresh closure on the very next render, which
      // it reads as one more detach, and that is an infinite loop rather than a lifecycle (D-125).
      const liveKeys = new Set(windows.map((w) => w.instanceKey));
      for (const key of refCallbacks.current.keys()) {
        if (!liveKeys.has(key)) refCallbacks.current.delete(key);
      }

      setEntries((prev) => {
        const live = new Map(windows.map((w) => [w.instanceKey, w]));
        const kept = prev.filter((e) => live.has(e.instanceKey));
        const known = new Set(kept.map((e) => e.instanceKey));
        const added = windows
          .filter((w) => !known.has(w.instanceKey))
          .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))
          .map((w) => ({ instanceKey: w.instanceKey, winId: w.id, appId: w.appId }));
        if (added.length === 0 && kept.length === prev.length) return prev;
        return [...kept, ...added];
      });
      layout();
    };

    sync();
    return wm.store.subscribe(sync);
  }, [wm, layout]);

  // Newly mounted nodes need their first layout before paint.
  useLayoutEffect(layout);

  // Counted for §20 alongside the frame layer: this one must *not* re-render during a drag at
  // all, because the set of live hosts does not change (§5.7).
  useEffect(() => {
    noteRender();
  });

  const refFor = (entry: HostEntry): ((node: HTMLDivElement | null) => void) => {
    // A fresh closure each render would make React detach and re-attach the ref, which reads
    // as an unmount to `onHostAttached`. One stable callback per instance key instead.
    const existing = refCallbacks.current.get(entry.instanceKey);
    if (existing) return existing;

    const callback = (node: HTMLDivElement | null): void => {
      if (node) {
        const chrome = node.querySelector<HTMLDivElement>(':scope > .win-chrome');
        const content = node.querySelector<HTMLDivElement>(':scope > .win-content');
        if (!chrome || !content) return;

        const entrySlots: HostSlots = { host: node, chrome, content };
        slots.current.set(entry.instanceKey, entrySlots);

        const win = wm.get(entry.winId);
        const state = wm.getState();
        if (win) applyHostLayout(node, win, state.activeDesktopId, state.focusedId);
        if (win && !attached.current.has(entry.instanceKey)) {
          attached.current.add(entry.instanceKey);
          onHostAttached?.(win, entrySlots);
        }
      } else {
        // The callback itself is deliberately *not* forgotten here; the store sync above does
        // that, once the window is really gone (D-125).
        slots.current.delete(entry.instanceKey);
        if (attached.current.delete(entry.instanceKey)) onHostDetached?.(entry.instanceKey);
      }
      onSlotsChanged?.(new Map(slots.current));
    };
    refCallbacks.current.set(entry.instanceKey, callback);
    return callback;
  };

  return (
    <div className="win-host-layer" data-testid="win-host-layer">
      {entries.map((entry) => (
        <div
          key={entry.instanceKey}
          ref={refFor(entry)}
          className={WIN_HOST_CLASS}
          data-instance-key={entry.instanceKey}
          data-window-id={entry.winId}
          data-app-id={entry.appId}
        >
          <div className="win-chrome" />
          <div className="win-content" />
        </div>
      ))}
    </div>
  );
}
